import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";
import useModuleAccess from "../hooks/useModuleAccess";

const API_URL = process.env.REACT_APP_API_URL;

const PAYMENT_STAT_CARDS = [
  {
    key: "totalPayments",
    label: "Total Payment",
    titleColor: "#127aea",
    valueColor: "#2c7fd8",
    filterValue: null,
  },
  {
    key: "paidPayments",
    label: "Paid",
    titleColor: "#196F3D",
    valueColor: "#27AE60",
    filterValue: "Paid",
  },
  {
    key: "pendingPayments",
    label: "Pending",
    titleColor: "#7D6608",
    valueColor: "#F39C12",
    filterValue: "Pending",
  },
  {
    key: "cancelledPayments",
    label: "Cancelled",
    titleColor: "#C0392B",
    valueColor: "#E74C3C",
    filterValue: "Cancelled",
  },
];

const PAYMENT_STATUS_FILTER_OPTIONS = PAYMENT_STAT_CARDS.filter(
  (card) => card.filterValue,
).map((card) => ({ value: card.filterValue, label: card.label }));

const DEFAULT_FILTER_CHECKS = {
  status: false,
  user: false,
  dateRange: false,
};
const DEFAULT_FILTER_INPUTS = {
  status: "",
  user: "",
  fromDate: "",
  toDate: "",
};
const DEFAULT_ACTIVE_MULTI_FILTERS = {
  status: "",
  user: "",
  fromDate: "",
  toDate: "",
};

const MAX_ACTIVE_FILTERS = 2;

const PaymentStatCardSkeleton = () => (
  <div className="tracker-stat-col">
    <div className="card supplier-stat-card">
      <div className="card-body">
        <div className="stat-skeleton stat-skeleton-title"></div>
        <div className="stat-skeleton stat-skeleton-value"></div>
      </div>
    </div>
  </div>
);

const PaymentStatCard = ({
  label,
  value,
  titleColor,
  valueColor,
  onClick,
  isActive,
}) => (
  <div className="tracker-stat-col">
    <div
      className={`card supplier-stat-card procurement-stat-card-clickable ${
        isActive ? "procurement-stat-card-active" : ""
      }`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      role="button"
      tabIndex={0}
      title={`Filter by ${label}`}
    >
      <div className="card-body">
        <div className="stat-title" style={{ color: titleColor }}>
          {label}
        </div>

        <div className="stat-value" style={{ color: valueColor }}>
          {value}
        </div>
      </div>
    </div>
  </div>
);

const formatCompactINR = (amount) => {
  const num = Number(amount) || 0;
  const absNum = Math.abs(num);
  if (absNum >= 1e7) return `₹${(num / 1e7).toFixed(2)} Cr`;
  if (absNum >= 1e5) return `₹${(num / 1e5).toFixed(2)} L`;
  if (absNum >= 1e3) return `₹${(num / 1e3).toFixed(2)} K`;
  return `₹${num.toLocaleString("en-IN")}`;
};

const formatFullINR = (amount) => {
  const num = Number(amount) || 0;
  return `₹${num.toLocaleString("en-IN")}`;
};

const STATUS_AMOUNT_KEY_MAP = {
  Paid: "paidAmount",
  Pending: "pendingAmount",
  Cancelled: "cancelledAmount",
};

const TotalAmountStatCard = ({ label, amount }) => (
  <div className="tracker-stat-col">
    <div className="card supplier-stat-card" title={formatFullINR(amount)}>
      <div className="card-body">
        <div className="stat-title" style={{ color: "#5B2C6F" }}>
          {label}
        </div>
        <div className="stat-value" style={{ color: "#8E44AD" }}>
          {formatCompactINR(amount)}
        </div>
      </div>
    </div>
  </div>
);

const PaymentTracker = () => {
  const { canCreate, canView, canUpdate, canDelete } =
    useModuleAccess("payment_tracker");
  const savedFilters = JSON.parse(
    sessionStorage.getItem("paymentTrackerFilters") || "{}",
  );
  const navigate = useNavigate();
  const currentUserRole = (localStorage.getItem("role") || "").toLowerCase();
  const isAdmin = currentUserRole === "admin";
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deletePaymentId, setDeletePaymentId] = useState(null);
  const [deletePaymentRef, setDeletePaymentRef] = useState("");
  const [searchQuery, setSearchQuery] = useState(
    savedFilters.searchQuery || "",
  ); // Input field value
  const [activeSearchQuery, setActiveSearchQuery] = useState(
    savedFilters.activeSearchQuery || "",
  ); // Actual search term that triggers API
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(savedFilters.currentPage || 1);
  const [itemsPerPage, setItemsPerPage] = useState(
    savedFilters.itemsPerPage || 25,
  );
  const [isDeleting, setIsDeleting] = useState(false);

  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [filterChecks, setFilterChecks] = useState({
    ...DEFAULT_FILTER_CHECKS,
    ...(savedFilters.filterChecks || {}),
  });
  const [filterInputs, setFilterInputs] = useState({
    ...DEFAULT_FILTER_INPUTS,
    ...(savedFilters.filterInputs || {}),
  });
  const [activeMultiFilters, setActiveMultiFilters] = useState({
    ...DEFAULT_ACTIVE_MULTI_FILTERS,
    ...(savedFilters.activeMultiFilters || {}),
  });
  const filterPanelRef = useRef(null);

  const [allUniqueUsers, setAllUniqueUsers] = useState([]); // Store all unique users for filter dropdown
  const [isExporting, setIsExporting] = useState(false); // Export loading state
  const [selectedPaymentIds, setSelectedPaymentIds] = useState([]);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [commentsModalText, setCommentsModalText] = useState(null);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [paymentStatus, setPaymentStatus] = useState({
    totalPayments: 0,
    paidPayments: 0,
    cancelledPayments: 0,
    pendingPayments: 0,
    totalAmount: 0,
    paidAmount: 0,
    pendingAmount: 0,
    cancelledAmount: 0,
  });

  const [isPaymentStatusLoading, setIsPaymentStatusLoading] = useState(true);

  const anyMultiFilterActive =
    !!activeMultiFilters.status ||
    !!activeMultiFilters.user ||
    !!activeMultiFilters.fromDate ||
    !!activeMultiFilters.toDate;

  const columnMap = {
    supplierName: "supplierName",
    beneficiaryAccountNumber: "beneficiaryAccountNumber",
    beneficiaryIfscNumber: "beneficiaryIfscNumber",
    debit: "debit",
    credit: "credit",
    amount: "amount",
    paymentStatus: "paymentStatus",
    createdBy: "createdBy",
    updatedBy: "updatedBy",
    createdAt: "createdAt",
    requestStatus: "requestStatus",
    createdOn: "createdAt",
    paymentDate: "paymentDate",
  };

  const handleSort = (column) => {
    const apiField = columnMap[column] || column;

    if (sortBy === apiField) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else {
        setSortBy("createdAt");
        setSortOrder("desc");
      }
    } else {
      setSortBy(apiField);
      setSortOrder("asc");
    }

    setCurrentPage(1);
  };

  const getSortIcon = (column) => {
    const apiField = columnMap[column] || column;

    if (sortBy !== apiField) {
      return (
        <i
          className="fa-solid fa-sort text-muted ml-1"
          style={{ fontSize: "0.8em" }}
        />
      );
    }

    if (sortOrder === "asc") {
      return (
        <i
          className="fa-solid fa-sort-up text-primary ml-1"
          style={{ fontSize: "0.8em" }}
        />
      );
    }

    return (
      <i
        className="fa-solid fa-sort-down text-primary ml-1"
        style={{ fontSize: "0.8em" }}
      />
    );
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        filterPanelRef.current &&
        !filterPanelRef.current.contains(e.target)
      ) {
        setIsFilterPanelOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  
  function toApiDDMMYYYY(isoDate) {
    if (!isoDate) return "";
    const [yyyy, mm, dd] = isoDate.split("-");
    if (!yyyy || !mm || !dd) return "";
    return `${dd}-${mm}-${yyyy}`;
  }

  const buildMultiFilterFields = useCallback(
    (fields = {}) => {
      if (activeMultiFilters.status) {
        fields.paymentStatus = activeMultiFilters.status;
      }
      if (activeMultiFilters.user) {
        fields.userId = activeMultiFilters.user;
      }
      if (activeMultiFilters.fromDate) {
        fields.fromDate = toApiDDMMYYYY(activeMultiFilters.fromDate);
      }
      if (activeMultiFilters.toDate) {
        fields.toDate = toApiDDMMYYYY(activeMultiFilters.toDate);
      }
      return fields;
    },
    [activeMultiFilters],
  );

  // Fetch payments with server-side pagination
  const fetchPayments = useCallback(async () => {
    setIsLoading(true);

    try {
      const payload = {
        action: "get-all-payments",
        page: currentPage,
        limit: itemsPerPage,
        sortBy,
        sortOrder,
      };

      // Add search parameter if activeSearchQuery has a value (not empty)
      const trimmedSearch = activeSearchQuery ? activeSearchQuery.trim() : "";
      if (trimmedSearch) {
        payload.search = trimmedSearch;
      }

      buildMultiFilterFields(payload);

      const response = await axios.post(API_URL, payload);

      // Parse the response - handle both string and object responses
      let responseData = response.data;
      if (typeof responseData === "string") {
        try {
          responseData = JSON.parse(responseData);
        } catch (parseError) {
          responseData = response.data;
        }
      }

      // Expecting: { success, data: { payments: [...], pagination: { total, page, limit } } }
      if (
        responseData.success === true &&
        responseData.data &&
        responseData.data.payments
      ) {
        const apiPayments = responseData.data.payments;
        setPayments(apiPayments);
        setFilteredPayments(apiPayments);

        if (
          responseData.data.pagination &&
          responseData.data.pagination.total
        ) {
          setTotalCount(responseData.data.pagination.total);
        } else {
          setTotalCount(apiPayments.length);
        }
      } else {
        toast.error(responseData.message || "Failed to fetch payment entries", {
          autoClose: 2000,
        });
        setPayments([]);
        setFilteredPayments([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Fetch payments error:", error);
      if (error.response && error.response.data) {
        let errorData = error.response.data;
        if (typeof errorData === "string") {
          try {
            errorData = JSON.parse(errorData);
          } catch (e) {
            // Use error message as is
          }
        }
        toast.error(
          errorData.message ||
            "An error occurred while fetching payment entries",
          { autoClose: 2000 },
        );
      } else {
        toast.error(
          "Network error. Please check your connection and try again.",
          { autoClose: 2000 },
        );
      }
      setPayments([]);
      setFilteredPayments([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    itemsPerPage,
    activeSearchQuery,
    sortBy,
    sortOrder,
    buildMultiFilterFields,
  ]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  useEffect(() => {
    if (commentsModalText == null || !window.$) return;
    const $m = window.$("#modalPaymentComments");
    if (!$m.length) return;
    $m.modal("show");
  }, [commentsModalText]);

  useEffect(() => {
    if (!window.$) return;
    const $m = window.$("#modalPaymentComments");
    if (!$m.length) return;
    const onHidden = () => setCommentsModalText(null);
    $m.on("hidden.bs.modal.comments", onHidden);
    return () => $m.off("hidden.bs.modal.comments", onHidden);
  }, []);

  // Clear bulk selections when list criteria changes (search/filter/page size)
  useEffect(() => {
    setSelectedPaymentIds([]);
  }, [activeSearchQuery, activeMultiFilters, itemsPerPage]);

  const fetchPaymentStatus = useCallback(async () => {
    setIsPaymentStatusLoading(true);
    try {
      const payload = { action: "payment-count" };

  
      if (activeMultiFilters.user) {
        payload.userId = activeMultiFilters.user;
      }
      if (activeMultiFilters.fromDate) {
        payload.fromDate = toApiDDMMYYYY(activeMultiFilters.fromDate);
      }
      if (activeMultiFilters.toDate) {
        payload.toDate = toApiDDMMYYYY(activeMultiFilters.toDate);
      }

      const response = await axios.post(API_URL, payload);

      let responseData = response.data;
      if (typeof responseData === "string") {
        responseData = JSON.parse(responseData);
      }

      const stats = responseData?.data || {};

      setPaymentStatus({
        totalPayments: Number(stats.totalCount) || 0,
        paidPayments: Number(stats.paid) || 0,
        pendingPayments: Number(stats.pending) || 0,
        cancelledPayments: Number(stats.cancelled) || 0,
        totalAmount: Number(stats.totalAmount) || 0,
        paidAmount: Number(stats.paidAmount) || 0,
        pendingAmount: Number(stats.pendingAmount) || 0,
        cancelledAmount: Number(stats.cancelledAmount) || 0,
      });
    } catch (error) {
      console.error("Payment stats error:", error);
      setPaymentStatus({
        totalPayments: 0,
        paidPayments: 0,
        pendingPayments: 0,
        cancelledPayments: 0,
        totalAmount: 0,
        paidAmount: 0,
        pendingAmount: 0,
        cancelledAmount: 0,
      });
    } finally {
      setIsPaymentStatusLoading(false);
    }
  }, [
    activeMultiFilters.user,
    activeMultiFilters.fromDate,
    activeMultiFilters.toDate,
  ]);

  useEffect(() => {
    fetchPaymentStatus();
  }, [fetchPaymentStatus]);

  const fetchPaymentUsers = useCallback(async () => {
    try {
      const payload = {
        action: "get-payment-users",
      };

      const response = await axios.post(API_URL, payload);

      let responseData = response.data;

      if (typeof responseData === "string") {
        responseData = JSON.parse(responseData);
      }

      if (responseData.success) {
        setAllUniqueUsers(responseData.data || []);
      }
    } catch (error) {
      console.error("Fetch payment users error:", error);
    }
  }, []);

  useEffect(() => {
    void fetchPaymentUsers();
  }, [fetchPaymentUsers]);

  const getDisplayedAmount = () => {
    if (activeMultiFilters.status) {
      return (
        paymentStatus[STATUS_AMOUNT_KEY_MAP[activeMultiFilters.status]] ?? 0
      );
    }
    return paymentStatus.totalAmount;
  };

  
  const handleFilterCheckToggle = (key) => {
    setFilterChecks((prev) => {
      const isCurrentlyChecked = prev[key];

      if (!isCurrentlyChecked) {
        const activeCount = Object.values(prev).filter(Boolean).length;
        if (activeCount >= MAX_ACTIVE_FILTERS) {
          toast.error(
            `You can select up to ${MAX_ACTIVE_FILTERS} filters only`,
            {
              autoClose: 2000,
            },
          );
          return prev;
        }
      }

      const next = { ...prev, [key]: !isCurrentlyChecked };

      if (!next[key]) {
        if (key === "dateRange") {
          setFilterInputs((inputs) => ({
            ...inputs,
            fromDate: "",
            toDate: "",
          }));
          setActiveMultiFilters((active) => ({
            ...active,
            fromDate: "",
            toDate: "",
          }));
        } else {
          setFilterInputs((inputs) => ({ ...inputs, [key]: "" }));
          setActiveMultiFilters((active) => ({ ...active, [key]: "" }));
        }
        if (currentPage !== 1) {
          setCurrentPage(1);
        }
      }

      return next;
    });
  };


  const handleFilterInputChange = (key, value) => {
    if (key === "fromDate" || key === "toDate") {
      const nextFrom = key === "fromDate" ? value : filterInputs.fromDate;
      const nextTo = key === "toDate" ? value : filterInputs.toDate;

      if (nextFrom && nextTo && new Date(nextFrom) > new Date(nextTo)) {
        toast.error("From date cannot be greater than to date", {
          autoClose: 2000,
        });
        return;
      }

      setFilterInputs((prev) => ({ ...prev, [key]: value }));
      setActiveMultiFilters((prev) => ({
        ...prev,
        fromDate: nextFrom,
        toDate: nextTo,
      }));
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
      return;
    }

    setFilterInputs((prev) => ({ ...prev, [key]: value }));
    setActiveMultiFilters((prev) => ({ ...prev, [key]: value }));
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  // Clear filter
  const handleClearFilter = () => {
    setFilterChecks(DEFAULT_FILTER_CHECKS);
    setFilterInputs(DEFAULT_FILTER_INPUTS);
    setActiveMultiFilters(DEFAULT_ACTIVE_MULTI_FILTERS);
    setIsFilterPanelOpen(false);
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  // Handle export with current filters
  const handleExport = async () => {
    setIsExporting(true);

    try {
      const payload = {
        action: "export-payments",
      };

      const trimmedSearch = activeSearchQuery?.trim();
      if (trimmedSearch) payload.search = trimmedSearch;

      buildMultiFilterFields(payload);

      const response = await axios.post(API_URL, payload, {
        responseType: "json",
      });

      let base64;

      if (response.data.body) {
        base64 = response.data.body;
      } else {
        base64 = response.data;
      }

      if (!base64 || !base64.startsWith("UEs")) {
        throw new Error("Invalid Excel data received");
      }

      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);

      const blob = new Blob([byteArray], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `payment-export-${Date.now()}.xlsx`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);

      toast.success("Export completed successfully", { autoClose: 2000 });
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export data. Please try again.", {
        autoClose: 2000,
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Calculate total pages
  // const totalPages = Math.ceil(totalCount / itemsPerPage)
  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage) || 1);
  const startItem =
    payments.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem = Math.min(currentPage * itemsPerPage, totalCount);

  // Handle items per page change
  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value, 10);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Handle page change
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);

      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }, 100);
    }
  };

  useEffect(() => {
    const filters = {
      searchQuery,
      activeSearchQuery,
      currentPage,
      itemsPerPage,
      filterChecks,
      filterInputs,
      activeMultiFilters,
    };

    sessionStorage.setItem("paymentTrackerFilters", JSON.stringify(filters));
  }, [
    searchQuery,
    activeSearchQuery,
    currentPage,
    itemsPerPage,
    filterChecks,
    filterInputs,
    activeMultiFilters,
  ]);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7; // Show up to 7 page numbers

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push("ellipsis");
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("ellipsis");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Handle search input change - if field becomes empty, automatically reset search
  const handleSearchChange = (e) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);

    const trimmedValue = newValue ? newValue.trim() : "";
    if (trimmedValue === "" && activeSearchQuery !== "") {
      setActiveSearchQuery("");
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
    }
  };

  // Handle search trigger - on Enter key or search icon click
  const handleSearchTrigger = () => {
    const trimmedQuery = searchQuery ? searchQuery.trim() : "";

    if (trimmedQuery === "") {
      setActiveSearchQuery("");
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
    } else {
      setActiveSearchQuery(trimmedQuery);
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
    }
  };

  // Handle Enter key press in search input
  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchTrigger();
    }
  };

  // Handle delete button click
  const handleDeleteClick = (paymentId, paymentRef) => {
    setDeletePaymentId(paymentId);
    setDeletePaymentRef(paymentRef);
  };

  // Handle delete confirmation
  const handleDelete = async () => {
    if (!deletePaymentId) {
      return;
    }

    const currentUserId = localStorage.getItem("id");
    if (!currentUserId) {
      toast.error("User session expired. Please login again.", {
        autoClose: 2000,
      });
      setTimeout(() => {
        navigate("/");
      }, 2000);
      return;
    }

    setIsDeleting(true);

    try {
      const payload = {
        action: "delete-payment",
        id: deletePaymentId,
        requestUserId: currentUserId,
      };

      const response = await axios.post(API_URL, payload);

      let responseData = response.data;
      if (typeof responseData === "string") {
        try {
          responseData = JSON.parse(responseData);
        } catch (parseError) {
          responseData = response.data;
        }
      }

      if (responseData.success === true) {
        toast.success("Deleted successfully", { autoClose: 2000 });

        if (window.$) {
          window.$("#modaldeletePayment").modal("hide");
        }

        setDeletePaymentId(null);
        setDeletePaymentRef("");

        if (payments.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchPayments();
        }
      } else {
        toast.error(responseData.message || "Failed to delete payment entry", {
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Delete payment error:", error);
      if (error.response && error.response.data) {
        let errorData = error.response.data;
        if (typeof errorData === "string") {
          try {
            errorData = JSON.parse(errorData);
          } catch (e) {
            // Use error message as is
          }
        }
        toast.error(
          errorData.message || "An error occurred while deleting payment entry",
          { autoClose: 2000 },
        );
      } else {
        toast.error(
          "Network error. Please check your connection and try again.",
          { autoClose: 2000 },
        );
      }
    } finally {
      setIsDeleting(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
  };

  const getTodayISODate = () => new Date().toISOString().slice(0, 10);

  const toggleSelectPayment = (paymentId) => {
    setSelectedPaymentIds((prev) =>
      prev.includes(paymentId)
        ? prev.filter((id) => id !== paymentId)
        : [...prev, paymentId],
    );
  };

  const toggleSelectAllVisible = () => {
    const visibleIds = filteredPayments.map((p) => p._id).filter(Boolean);
    const allSelected =
      visibleIds.length > 0 &&
      visibleIds.every((id) => selectedPaymentIds.includes(id));
    setSelectedPaymentIds((prev) => {
      if (visibleIds.length === 0) return prev;

      if (allSelected) {
        // Remove only visible ids, keep other page selections
        return prev.filter((id) => !visibleIds.includes(id));
      }

      // Add missing visible ids, keep existing selections
      const next = new Set(prev);
      visibleIds.forEach((id) => next.add(id));
      return Array.from(next);
    });
  };

  const handleBulkMarkPaid = async () => {
    if (!isAdmin) {
      toast.error("Access denied. Only admin can mark payments as Paid.", {
        autoClose: 2000,
      });
      return;
    }
    if (selectedPaymentIds.length === 0) {
      toast.error("Please select at least one payment", { autoClose: 2000 });
      return;
    }

    const currentUserId = localStorage.getItem("id");
    if (!currentUserId) {
      toast.error("User session expired. Please login again.", {
        autoClose: 2000,
      });
      setTimeout(() => {
        navigate("/");
      }, 2000);
      return;
    }

    const confirm = window.confirm(
      `Mark ${selectedPaymentIds.length} payment(s) as Paid?`,
    );
    if (!confirm) return;

    setIsBulkUpdating(true);
    try {
      const paymentDate = getTodayISODate();

      const selectedPayments = payments.filter((p) =>
        selectedPaymentIds.includes(p._id),
      );
      const updateRequests = selectedPayments.map((payment) => {
        const payload = {
          action: "update-payment",
          id: payment._id,
          supplierName:
            payment.supplierName || payment.supplierId?.clientname || "",
          supplierId: payment.supplierId?._id || payment.supplierId || "",
          beneficiaryAccountNumber: payment.beneficiaryAccountNumber || "",
          beneficiaryIfscNumber: payment.beneficiaryIfscNumber || "",
          amount: Number(payment.amount) || 0,
          debit: payment.debit || "",
          credit: payment.credit || "",
          requestStatus: payment.requestStatus || "Advance",
          paymentDate: payment.paymentDate
            ? new Date(payment.paymentDate).toISOString().slice(0, 10)
            : paymentDate,
          paymentStatus: "Paid",
          invoiceStatus: payment.invoiceStatus || "",
          comments: (payment.comments || "").trim(),
          updatedBy: currentUserId,
        };
        return axios.post(API_URL, payload);
      });

      const results = await Promise.allSettled(updateRequests);
      const successCount = results.filter(
        (r) => r.status === "fulfilled",
      ).length;
      const failCount = results.length - successCount;

      if (successCount > 0) {
        toast.success(`Updated ${successCount} payment(s) to Paid`, {
          autoClose: 2000,
        });
      }
      if (failCount > 0) {
        toast.error(`${failCount} payment(s) failed to update`, {
          autoClose: 2500,
        });
      }

      setSelectedPaymentIds([]);
      fetchPayments();
    } catch (error) {
      console.error("Bulk paid update error:", error);
      toast.error("Failed to update selected payments", { autoClose: 2000 });
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const handleBulkMarkAccounted = async () => {
    if (selectedPaymentIds.length === 0) {
      toast.error("Please select at least one payment", { autoClose: 2000 });
      return;
    }

    const currentUserId = localStorage.getItem("id");
    if (!currentUserId) {
      toast.error("User session expired. Please login again.", {
        autoClose: 2000,
      });
      setTimeout(() => {
        navigate("/");
      }, 2000);
      return;
    }

    const selectedPayments = payments.filter((p) =>
      selectedPaymentIds.includes(p._id),
    );
    const paidPayments = selectedPayments.filter(
      (p) => p.paymentStatus === "Paid",
    );

    if (paidPayments.length === 0) {
      toast.error("Only Paid payments can be marked as Accounted", {
        autoClose: 2000,
      });
      return;
    }

    const skippedCount = selectedPayments.length - paidPayments.length;
    const confirmMessage =
      skippedCount > 0
        ? `${skippedCount} selected payment(s) are not Paid and will be skipped. Mark ${paidPayments.length} payment(s) as Accounted?`
        : `Mark ${paidPayments.length} payment(s) as Accounted?`;

    if (!window.confirm(confirmMessage)) return;

    setIsBulkUpdating(true);
    try {
      const updateRequests = paidPayments.map((payment) => {
        const payload = {
          action: "update-payment",
          id: payment._id,
          paymentStatus: "Paid",
          invoiceStatus: "Accounted",
          comments: (payment.comments || "").trim(),
          updatedBy: currentUserId,
        };
        return axios.post(API_URL, payload);
      });

      const results = await Promise.allSettled(updateRequests);
      const successCount = results.filter(
        (r) => r.status === "fulfilled",
      ).length;
      const failCount = results.length - successCount;

      if (successCount > 0) {
        toast.success(`Updated ${successCount} payment(s) to Accounted`, {
          autoClose: 2000,
        });
      }
      if (failCount > 0) {
        toast.error(`${failCount} payment(s) failed to update`, {
          autoClose: 2500,
        });
      }

      setSelectedPaymentIds([]);
      fetchPayments();
    } catch (error) {
      console.error("Bulk accounted update error:", error);
      toast.error("Failed to update selected payments", { autoClose: 2000 });
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const getInvoiceStatusColor = (status) => {
    if (!status) return "";
    const statusLower = status.toLowerCase();

    switch (statusLower) {
      case "accounted":
        return "f_green";
      case "pending":
        return "f_orange";
      default:
        return "";
    }
  };

  // Get payment status color class
  const getPaymentStatusColor = (status) => {
    if (!status) return "f_orange";
    const statusLower = status.toLowerCase();

    switch (statusLower) {
      case "paid":
        return "f_green";
      case "pending":
        return "f_orange";
      case "overdue":
        return "f_red";
      case "cancelled":
        return "text-muted";
      default:
        return "f_orange";
    }
  };

  const isPaymentStatCardActive = (card) => {
    if (!card.filterValue) {
      return !activeMultiFilters.status;
    }
    return activeMultiFilters.status === card.filterValue;
  };
  const handlePaymentStatCardClick = (card) => {
    if (!card.filterValue) {
      setFilterChecks((prev) => ({ ...prev, status: false }));
      setFilterInputs((prev) => ({ ...prev, status: "" }));
      setActiveMultiFilters((prev) => ({ ...prev, status: "" }));
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
      return;
    }

    if (!filterChecks.status) {
      const otherActiveKeys = Object.keys(filterChecks).filter(
        (key) => key !== "status" && filterChecks[key],
      );

      if (otherActiveKeys.length >= MAX_ACTIVE_FILTERS) {
        const keyToRemove = otherActiveKeys[0];
        const clearRemovedInputs =
          keyToRemove === "dateRange"
            ? { fromDate: "", toDate: "" }
            : { [keyToRemove]: "" };
        setFilterChecks((prev) => ({
          ...prev,
          [keyToRemove]: false,
          status: true,
        }));
        setFilterInputs((prev) => ({
          ...prev,
          ...clearRemovedInputs,
          status: card.filterValue,
        }));
        setActiveMultiFilters((prev) => ({
          ...prev,
          ...clearRemovedInputs,
          status: card.filterValue,
        }));
        toast.info("Replaced an existing filter to apply Status", {
          autoClose: 2000,
        });
      } else {
        setFilterChecks((prev) => ({ ...prev, status: true }));
        setFilterInputs((prev) => ({ ...prev, status: card.filterValue }));
        setActiveMultiFilters((prev) => ({
          ...prev,
          status: card.filterValue,
        }));
      }
    } else {
      setFilterInputs((prev) => ({ ...prev, status: card.filterValue }));
      setActiveMultiFilters((prev) => ({ ...prev, status: card.filterValue }));
    }

    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0 txt_lft">Payment Tracker</h1>
            </div>
            <div className="col-sm-6 txt_rgt">
              {canCreate && (
                <Link to="/paymentTrackerAdd" className="btn btn-blue">
                  Add
                </Link>
              )}
              {isAdmin && (
                <button
                  type="button"
                  className="btn btn-primary btn-sm ml-1"
                  onClick={handleBulkMarkPaid}
                  disabled={isBulkUpdating || selectedPaymentIds.length === 0}
                  title="Mark selected payments as Paid"
                >
                  <i
                    className={`fa-solid ${isBulkUpdating ? "fa-spinner fa-spin" : "fa-check"} mr-1`}
                  ></i>
                  {isBulkUpdating
                    ? "Updating..."
                    : `Paid (${selectedPaymentIds.length})`}
                </button>
              )}
              <button
                type="button"
                className="btn btn-info btn-sm ml-1"
                onClick={handleBulkMarkAccounted}
                disabled={isBulkUpdating || selectedPaymentIds.length === 0}
                title="Mark selected Paid payments as Accounted"
              >
                <i
                  className={`fa-solid ${isBulkUpdating ? "fa-spinner fa-spin" : "fa-book"} mr-1`}
                ></i>
                {isBulkUpdating
                  ? "Updating..."
                  : `Accounted (${selectedPaymentIds.length})`}
              </button>
              <button
                type="button"
                className="btn btn-success btn-sm ml-1"
                onClick={handleExport}
                disabled={isExporting}
              >
                <i
                  className={`fa-solid ${isExporting ? "fa-spinner fa-spin" : "fa-file-export"} mr-1`}
                ></i>
                {isExporting ? "Exporting..." : "Export"}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="procurement-stat-card-row">
                {isPaymentStatusLoading ? (
                  <>
                    {PAYMENT_STAT_CARDS.map((card) => (
                      <PaymentStatCardSkeleton key={card.key} />
                    ))}
                    <PaymentStatCardSkeleton key="totalAmount" />
                  </>
                ) : (
                  <>
                    {PAYMENT_STAT_CARDS.map((card) => (
                      <PaymentStatCard
                        key={card.key}
                        label={card.label}
                        value={paymentStatus[card.key]}
                        titleColor={card.titleColor}
                        valueColor={card.valueColor}
                        isActive={isPaymentStatCardActive(card)}
                        onClick={() => handlePaymentStatCardClick(card)}
                      />
                    ))}
                    <TotalAmountStatCard
                      label="Total Amount"
                      amount={getDisplayedAmount()}
                    />
                  </>
                )}
              </div>
              <div className="card-body">
                <div className="row align-items-end mb-3 flex-lg-nowrap">
                  <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2 mb-lg-0">
                    <div className="form-group mb-0">
                      <label
                        className="mb-1"
                        style={{ fontSize: "13px", fontWeight: "500" }}
                      >
                        Search
                      </label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search payments..."
                          value={searchQuery}
                          onChange={handleSearchChange}
                          onKeyPress={handleSearchKeyPress}
                        />
                        <div className="input-group-append">
                          <div
                            className="input-group-text crsr_pntr"
                            onClick={handleSearchTrigger}
                            style={{ cursor: "pointer" }}
                          >
                            <i className="fa-solid fa-search" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-sm-6 col-md-4 col-lg-2 mb-2 mb-lg-0">
                    <div
                      className="form-group mb-0 position-relative"
                      ref={filterPanelRef}
                    >
                      <label
                        className="mb-1"
                        style={{ fontSize: "13px", fontWeight: "500" }}
                      >
                        Filter
                      </label>

                      <button
                        type="button"
                        className="form-control text-left d-flex justify-content-between align-items-center"
                        onClick={() => setIsFilterPanelOpen((open) => !open)}
                      >
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {anyMultiFilterActive ? "Filters" : "Select Filters"}
                        </span>

                        <i
                          className={`fa-solid fa-chevron-${
                            isFilterPanelOpen ? "up" : "down"
                          }`}
                          style={{
                            fontSize: "11px",
                            flexShrink: 0,
                            marginLeft: "6px",
                          }}
                        />
                      </button>

                      {isFilterPanelOpen && (
                        <div
                          className="procurement-filter-dropdown"
                          style={{
                            position: "absolute",
                            zIndex: 20,
                            top: "100%",
                            left: 0,
                            marginTop: "4px",
                            background: "#fff",
                            border: "1px solid #ced4da",
                            borderRadius: "4px",
                            padding: "10px 12px",
                            width: "190px",
                            maxWidth: "calc(100vw - 32px)",
                            boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                          }}
                        >
                          {[
                            {
                              key: "status",
                              id: "filterCheckStatus",
                              label: "Status",
                            },
                            {
                              key: "user",
                              id: "filterCheckUser",
                              label: "User",
                            },
                            {
                              key: "dateRange",
                              id: "filterCheckDateRange",
                              label: "Date",
                            },
                          ].map((filter) => {
                            const activeCount =
                              Object.values(filterChecks).filter(
                                Boolean,
                              ).length;

                            return (
                              <div
                                className="custom-control custom-checkbox mb-2"
                                key={filter.key}
                              >
                                <input
                                  type="checkbox"
                                  className="custom-control-input"
                                  id={filter.id}
                                  checked={filterChecks[filter.key]}
                                  disabled={
                                    !filterChecks[filter.key] &&
                                    activeCount >= MAX_ACTIVE_FILTERS
                                  }
                                  onChange={() =>
                                    handleFilterCheckToggle(filter.key)
                                  }
                                />

                                <label
                                  className="custom-control-label"
                                  htmlFor={filter.id}
                                >
                                  {filter.label}
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {filterChecks.status && (
                    <div className="col-12 col-sm-6 col-md-4 col-lg-2 mb-2 mb-lg-0">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{ fontSize: "13px", fontWeight: "500" }}
                        >
                          Status
                        </label>

                        <select
                          className="form-control"
                          value={filterInputs.status}
                          onChange={(e) =>
                            handleFilterInputChange("status", e.target.value)
                          }
                        >
                          <option value="">Select Status</option>

                          {PAYMENT_STATUS_FILTER_OPTIONS.map((status) => (
                            <option key={status.value} value={status.value}>
                              {status.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {filterChecks.user && (
                    <div className="col-12 col-sm-6 col-md-4 col-lg-2 mb-2 mb-lg-0">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{ fontSize: "13px", fontWeight: "500" }}
                        >
                          User
                        </label>

                        <select
                          className="form-control"
                          value={filterInputs.user}
                          onChange={(e) =>
                            handleFilterInputChange("user", e.target.value)
                          }
                        >
                          <option value="">Select User</option>
                          {allUniqueUsers.map((user) => (
                            <option key={user._id} value={user._id}>
                              {user.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {filterChecks.dateRange && (
                    <>
                      <div className="col-12 col-sm-6 col-md-4 col-lg-2 mb-2 mb-lg-0">
                        <div className="form-group mb-0">
                          <label
                            className="mb-1"
                            style={{ fontSize: "13px", fontWeight: "500" }}
                          >
                            From Date
                          </label>
                          <input
                            type="date"
                            className="form-control"
                            value={filterInputs.fromDate || ""}
                            max={filterInputs.toDate || undefined}
                            onChange={(e) =>
                              handleFilterInputChange(
                                "fromDate",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                      </div>
                      <div className="col-12 col-sm-6 col-md-4 col-lg-2 mb-2 mb-lg-0">
                        <div className="form-group mb-0">
                          <label
                            className="mb-1"
                            style={{ fontSize: "13px", fontWeight: "500" }}
                          >
                            To Date
                          </label>
                          <input
                            type="date"
                            className="form-control"
                            value={filterInputs.toDate || ""}
                            min={filterInputs.fromDate || undefined}
                            onChange={(e) =>
                              handleFilterInputChange("toDate", e.target.value)
                            }
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {anyMultiFilterActive && (
                    <div className="col-12 col-sm-6 col-md-4 col-lg-auto mb-2 mb-lg-0">
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm w-100"
                        onClick={handleClearFilter}
                        style={{
                          whiteSpace: "nowrap",
                        }}
                      >
                        <i className="fa-solid fa-times mr-1" />
                        Clear
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {isLoading ? (
                <div className="text-center p-4">
                  <PulseLoader color="#1199B2" size={10} />
                </div>
              ) : payments.length === 0 ? (
                <div className="nocontent_div text-center p-4">
                  There are no payment entries yet.
                </div>
              ) : filteredPayments.length === 0 && activeSearchQuery ? (
                <div className="nocontent_div text-center p-4">
                  No payment entries found matching your search.
                  <div className="mt-3">
                    <button
                      className="btn btn-blue"
                      onClick={() => {
                        setSearchQuery("");
                        setActiveSearchQuery("");
                        setCurrentPage(1);
                      }}
                    >
                      Clear Search
                    </button>
                  </div>
                </div>
              ) : (
                <div className="supply-table-container">
                  <div className="table-responsive">
                    <table
                      id="payments"
                      width="100%"
                      className="table table-striped table-bordered txt_lft"
                    >
                      <thead>
                        <tr style={{ fontSize: "12px" }}>
                          <th style={{ fontSize: "12px", width: "45px" }}>
                            <input
                              type="checkbox"
                              checked={
                                filteredPayments.length > 0 &&
                                filteredPayments.every((p) =>
                                  selectedPaymentIds.includes(p._id),
                                )
                              }
                              onChange={toggleSelectAllVisible}
                              disabled={filteredPayments.length === 0}
                              title="Select all visible"
                            />
                          </th>
                          <th style={{ fontSize: "12px" }}>S.No</th>
                          <th
                            onClick={() => handleSort("supplierName")}
                            style={{ fontSize: "12px" }}
                          >
                            Supplier Name {getSortIcon("supplierName")}
                          </th>
                          <th style={{ fontSize: "12px" }}>Account Number</th>
                          <th style={{ fontSize: "12px" }}>IFSC Code</th>
                          <th
                            onClick={() => handleSort("amount")}
                            style={{ fontSize: "12px" }}
                          >
                            {" "}
                            Amount {getSortIcon("amount")}
                          </th>
                          <th
                            onClick={() => handleSort("requestStatus")}
                            style={{ fontSize: "12px" }}
                          >
                            {" "}
                            Request Status {getSortIcon("requestStatus")}
                          </th>
                          <th
                            onClick={() => handleSort("createdAt")}
                            style={{ fontSize: "12px" }}
                          >
                            Created On {getSortIcon("createdAt")}
                          </th>
                          <th
                            onClick={() => handleSort("paymentDate")}
                            style={{ fontSize: "12px" }}
                          >
                            Payment Date {getSortIcon("paymentDate")}
                          </th>
                          <th
                            onClick={() => handleSort("paymentStatus")}
                            style={{ fontSize: "12px" }}
                          >
                            {" "}
                            Payment Status {getSortIcon("paymentStatus")}
                          </th>
                          <th style={{ fontSize: "12px" }}>Invoice Status</th>
                          <th
                            onClick={() => handleSort("createdBy")}
                            style={{ fontSize: "12px" }}
                          >
                            {" "}
                            Created By {getSortIcon("createdBy")}
                          </th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredPayments.map((payment, index) => (
                          <tr
                            key={payment._id || index}
                            style={{ fontSize: "12px" }}
                          >
                            <td style={{ fontSize: "12px" }}>
                              <input
                                type="checkbox"
                                checked={selectedPaymentIds.includes(
                                  payment._id,
                                )}
                                onChange={() =>
                                  toggleSelectPayment(payment._id)
                                }
                                title="Select"
                              />
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              {(currentPage - 1) * itemsPerPage + index + 1}
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              {payment.supplierId?.clientname ||
                                payment.supplierName ||
                                "-"}
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              {payment.beneficiaryAccountNumber || "-"}
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              {payment.beneficiaryIfscNumber || "-"}
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              {payment.amount != null
                                ? payment.amount.toLocaleString()
                                : 0}
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              <span className="fwt_600">
                                {payment.requestStatus || "-"}
                              </span>
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              {formatDate(payment.createdAt)}
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              {formatDate(payment.paymentDate)}
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              <span
                                className={`fwt_600 ${getPaymentStatusColor(
                                  payment.paymentStatus,
                                )}`}
                              >
                                {payment.paymentStatus || "Pending"}
                              </span>
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              {payment.invoiceStatus ? (
                                <span
                                  className={`fwt_600 ${getInvoiceStatusColor(
                                    payment.invoiceStatus,
                                  )}`}
                                >
                                  {payment.invoiceStatus}
                                </span>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              {payment.createdBy?.name || "-"}
                            </td>
                            <td style={{ fontSize: "13px" }}>
                              <div className="d-flex justify-content-center align-items-center">
                                {payment.comments &&
                                  payment.comments.trim() && (
                                    <button
                                      type="button"
                                      className="crsr_pntr btn btn-link p-0 border-0 mr-2"
                                      onClick={() =>
                                        setCommentsModalText(
                                          payment.comments.trim(),
                                        )
                                      }
                                      title="View Comments"
                                      style={{
                                        color: "var(--blue, #0094a9)",
                                        textDecoration: "none",
                                      }}
                                    >
                                      <i
                                        className="fa-solid fa-comments"
                                        aria-hidden="true"
                                      />
                                    </button>
                                  )}
                                {canView && (
                                  <Link
                                    to={`/paymentView/${payment._id}`}
                                    className="crsr_pntr mr-2"
                                    title="View"
                                  >
                                    <i className="fa-solid fa-eye" />
                                  </Link>
                                )}
                                {canUpdate && (
                                  <Link
                                    to={`/paymentTrackerEdit/${payment._id}`}
                                    className="crsr_pntr mr-2"
                                    title="Edit"
                                  >
                                    <i className="fa-solid fa-pen" />
                                  </Link>
                                )}
                                {canDelete && (
                                  <button
                                    type="button"
                                    className="crsr_pntr p-0 border-0 bg-transparent"
                                    data-toggle="modal"
                                    data-target="#modaldeletePayment"
                                    onClick={() =>
                                      handleDeleteClick(
                                        payment._id,
                                        payment.supplierId?.clientname ||
                                          payment.supplierName,
                                      )
                                    }
                                    title="Delete"
                                    aria-label="Delete payment"
                                  >
                                    <i className="fa-solid fa-trash-can" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {!isLoading && payments.length > 0 && (
                    <div className="supply-pagination-container">
                      <div className="pagination-wrapper">
                        {/* Entries Section */}
                        <div className="pagination-info">
                          <label
                            className="mb-0 mr-2"
                            style={{ fontSize: "13px" }}
                          >
                            Show:
                          </label>

                          <select
                            className="form-control form-control-sm"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                          >
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                          </select>

                          <span className="ml-2" style={{ fontSize: "13px" }}>
                            entries
                          </span>

                          <span
                            className="ml-3 text-muted"
                            style={{ fontSize: "13px" }}
                          >
                            {startItem}–{endItem} of {totalCount}
                          </span>
                        </div>

                        {/* Pagination Buttons */}
                        <nav className="pagination-nav">
                          <ul className="pagination mb-0">
                            <li
                              className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                                title="First Page"
                              >
                                <i className="fa-solid fa-angle-double-left"></i>
                              </button>
                            </li>

                            <li
                              className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage - 1)
                                }
                                disabled={currentPage === 1}
                                title="Previous Page"
                              >
                                <i className="fa-solid fa-angle-left"></i>
                              </button>
                            </li>

                            {getPageNumbers().map((page, index) =>
                              page === "ellipsis" ? (
                                <li
                                  key={`ellipsis-${index}`}
                                  className="page-item disabled"
                                >
                                  <span className="page-link">...</span>
                                </li>
                              ) : (
                                <li
                                  key={page}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => handlePageChange(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              ),
                            )}

                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage + 1)
                                }
                                disabled={currentPage === totalPages}
                                title="Next Page"
                              >
                                <i className="fa-solid fa-angle-right"></i>
                              </button>
                            </li>

                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages}
                                title="Last Page"
                              >
                                <i className="fa-solid fa-angle-double-right"></i>
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Comments Modal */}
      <div
        className="modal fade"
        id="modalPaymentComments"
        tabIndex={-1}
        aria-labelledby="modalPaymentCommentsLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modalPaymentCommentsLabel">
                Comments
              </h5>
              {/* <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark" />
              </button> */}
            </div>
            <div className="modal-body">
              <div
                style={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  fontSize: "13px",
                  lineHeight: 1.5,
                }}
              >
                {commentsModalText || ""}
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                data-dismiss="modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Payment Modal */}
      <div
        className="modal fade"
        id="modaldeletePayment"
        tabIndex={-1}
        aria-labelledby="modaldeletePaymentLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modaldeletePaymentLabel">
                Delete Payment Entry
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              <div className="w-100 d-inline-block text-center">
                Are you sure you want to delete <br />
                <span className="fwt_600">{deletePaymentRef}</span>?
              </div>
            </div>
            <div className="modal-footer justify-content-center">
              <button
                type="button"
                className="btn btn-secondary mr-2"
                data-dismiss="modal"
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentTracker;
